import { InterpretationSignal, SajuCalculationResult } from './types';

const ELEMENT_LABELS = ['목', '화', '토', '금', '수'];

export function generateInterpretationSignals(result: SajuCalculationResult): InterpretationSignal[] {
    let signals: InterpretationSignal[] = [];
    let baseConfidence = confidenceValue(result.confidence ?? result.validation?.confidence ?? 1);
    let pillars = result.pillars || result.chart?.pillars || [];
    let dayPillar = pillars[2];
    let monthPillar = pillars[1];
    let timeUnknown = !!(result.normalizedInput?.timeUnknown || (result.warnings || []).some((warning: any) => warning.code === 'time_unknown'));

    if (dayPillar && !dayPillar.unknown) {
        let dayDetail = findPillarDetail(result, '일주');
        signals.push(signal({
            signalId: 'sig-day-pillar',
            category: 'day_pillar',
            label: `${dayPillar.label} 일주 / ${dayPillar.stem?.name || ''} 일간`,
            summary: '일간과 일지를 기본 성향 해석의 1차 근거로 분리합니다.',
            strength: 0.72,
            confidence: baseConfidence,
            polarity: 'neutral',
            domains: ['core_identity', 'temperament'],
            evidenceIds: compact([
                `pillar:day:${dayPillar.label}`,
                `dayMaster:${dayPillar.stem?.name || ''}`,
                `dayBranch:${dayPillar.branch?.name || ''}`,
                dayDetail?.branchTenGod ? `tenGod:dayBranch:${dayDetail.branchTenGod}` : '',
                dayDetail?.twelveStage ? `twelveStage:day:${dayDetail.twelveStage}` : ''
            ]),
            allowedClaims: [
                `${dayPillar.stem?.name || ''} 일간과 ${dayPillar.branch?.name || ''} 일지를 핵심 성향 근거로 사용할 수 있다.`,
                '일주 기반 해석은 월지, 오행 분포, 십성과 함께 교차 확인한다.'
            ],
            blockedClaims: [
                '일간 하나만으로 성격, 직업, 관계 결론을 확정하지 않는다.',
                '일주만 보고 대운, 재물, 결혼 결과를 강하게 단정하지 않는다.'
            ],
            meta: {
                pillar: dayPillar.label,
                stem: dayPillar.stem?.name,
                branch: dayPillar.branch?.name,
                stemElement: dayPillar.stem?.element,
                branchElement: dayPillar.branch?.element
            }
        }));
    }

    let elementSignal = buildElementBalanceSignal(result, timeUnknown ? timeLimitedConfidence(baseConfidence, 'element_balance') : baseConfidence, timeUnknown);
    if (elementSignal) signals.push(elementSignal);

    let tenGodSignal = buildDominantTenGodSignal(result, timeUnknown ? timeLimitedConfidence(baseConfidence, 'ten_god') : baseConfidence, timeUnknown);
    if (tenGodSignal) signals.push(tenGodSignal);

    let strengthSignal = buildStrengthSignal(result, timeUnknown ? timeLimitedConfidence(baseConfidence, 'strength') : baseConfidence, dayPillar, monthPillar, timeUnknown);
    if (strengthSignal) signals.push(strengthSignal);

    signals.push(buildInteractionSignal(result, timeUnknown ? timeLimitedConfidence(baseConfidence, 'interaction') : baseConfidence, timeUnknown));
    signals.push(buildStarSignal(result, timeUnknown ? timeLimitedConfidence(baseConfidence, 'star') : baseConfidence, timeUnknown));

    if (timeUnknown) {
        signals.push(signal({
            signalId: 'sig-time-unknown',
            category: 'warning',
            label: '생시 미정',
            summary: '시주, 시지, 시주 기반 십성·신살·합충·말년운·자녀운 해석을 제한해야 하는 입력 경고입니다.',
            strength: 0.92,
            confidence: clamp(baseConfidence * 0.85),
            polarity: 'challenging',
            domains: ['calculation_boundary', 'hour_pillar'],
            evidenceIds: ['warning:time_unknown', 'normalizedInput:timeUnknown:true'],
            allowedClaims: [
                '년주, 월주, 일주 중심으로 해석 범위를 좁힌다.',
                '시주, 말년운, 자녀운, 시지 기준 신살은 신뢰도 제한을 표시한다.',
                '직업 해석 중 시주가 보조하는 결실·후반부 역할 신호는 참고용으로만 둔다.'
            ],
            blockedClaims: [
                '시주가 확정된 것처럼 말하지 않는다.',
                '시지 기반 신살이나 말년운을 강하게 단정하지 않는다.',
                '시주 십성이나 시주 합충을 근거로 직업, 자녀, 말년 결론을 확정하지 않는다.'
            ],
            meta: { warningCode: 'time_unknown' }
        }));
    }

    return signals;
}

function buildElementBalanceSignal(result: SajuCalculationResult, baseConfidence: number, timeUnknown: boolean = false): InterpretationSignal | null {
    let elements = normalizeElements(result.elements || []);
    if (!elements.length) return null;

    let max = elements[0];
    let min = elements[elements.length - 1];
    let dominant = elements.filter((item: any) => item.value === max.value || item.percent >= 28);
    let weak = elements.filter((item: any) => item.value === 0 || item.percent <= 10);
    if (!weak.length && min && max && min.value < max.value * 0.45) weak = [min];
    let missing = elements.filter((item: any) => item.value === 0);
    let spread = max && min ? (max.percent || 0) - (min.percent || 0) : 0;

    return signal({
        signalId: 'sig-element-balance',
        category: 'element_balance',
        label: `오행 분포: ${dominant.map((item: any) => item.label).join('·')} 우세${weak.length ? ` / ${weak.map((item: any) => item.label).join('·')} 부족` : ''}`,
        summary: timeUnknown
            ? '과다/부족 오행은 성향의 강약과 보완 행동 후보를 판단하는 근거이지만, 생시 미정이라 시주 오행은 제외된 참고 분포입니다.'
            : '과다/부족 오행은 성향의 강약과 보완 행동 후보를 판단하는 근거입니다.',
        strength: clamp(0.38 + spread / 100),
        confidence: baseConfidence,
        polarity: weak.length || dominant.length > 1 ? 'mixed' : 'neutral',
        domains: ['element_balance', 'self_care', 'behavior'],
        evidenceIds: elements.map((item: any) => `element:${item.label}:value:${item.value}:percent:${item.percent || 0}`),
        allowedClaims: [
            `${dominant.map((item: any) => item.label).join('·')} 기운이 상대적으로 두드러진다고 볼 수 있다.`,
            weak.length ? `${weak.map((item: any) => item.label).join('·')} 기운은 보완 행동 후보로 볼 수 있다.` : '특정 부족 오행보다 전체 균형을 먼저 본다.',
            '오행 분포는 일간, 월지, 십성과 함께 해석한다.',
            timeUnknown ? '생시 미정에서는 오행 분포를 년주·월주·일주 기준 참고값으로 본다.' : ''
        ],
        blockedClaims: [
            '오행 하나만으로 건강, 사고, 실패를 단정하지 않는다.',
            '부족 오행만 보고 특정 직업이나 관계 결과를 확정하지 않는다.',
            '계산된 분포에 없는 오행 과다/부족을 만들어내지 않는다.',
            timeUnknown ? '시주 오행까지 포함한 전체 분포처럼 말하지 않는다.' : ''
        ],
        meta: {
            dominant: dominant.map((item: any) => item.label),
            weak: weak.map((item: any) => item.label),
            missing: missing.map((item: any) => item.label),
            spread: spread,
            timeUnknownLimited: timeUnknown
        }
    });
}

function buildDominantTenGodSignal(result: SajuCalculationResult, baseConfidence: number, timeUnknown: boolean = false): InterpretationSignal | null {
    let tenGods = (result.tenGods || []).filter((item: any) => item && item.value > 0);
    if (!tenGods.length) return null;
    let dominant = tenGods.find((item: any) => item.label !== '일간') || tenGods[0];
    let evidence = tenGods
        .slice(0, 4)
        .map((item: any) => `tenGod:${item.label}:value:${item.value}:percent:${item.percent || 0}`);

    return signal({
        signalId: 'sig-dominant-ten-god',
        category: 'ten_god',
        label: `중심 십성: ${dominant.label}`,
        summary: timeUnknown
            ? '십성 분포에서 가장 두드러지는 역할 신호를 분리하되, 생시 미정이라 시주 십성은 제외된 참고값입니다.'
            : '십성 분포에서 가장 두드러지는 역할 신호를 분리합니다.',
        strength: clamp((dominant.percent || 0) / 100 + 0.32),
        confidence: baseConfidence,
        polarity: 'mixed',
        domains: ['motivation', 'work_style', 'relationship'],
        evidenceIds: evidence,
        allowedClaims: [
            `${dominant.label} 성향을 주요 동기나 행동 방식 후보로 볼 수 있다.`,
            '중심 십성은 일간과 오행 균형을 함께 보며 해석한다.',
            timeUnknown ? '생시 미정에서는 시주 천간·시지 지장간 십성을 해석 근거에서 제외한다.' : ''
        ],
        blockedClaims: [
            '중심 십성 하나로 성격 전체를 단정하지 않는다.',
            '십성 분포만으로 직업, 재물, 결혼 결과를 확정하지 않는다.',
            timeUnknown ? '시주 십성을 근거로 말년운, 자녀운, 직업 결실을 단정하지 않는다.' : ''
        ],
        meta: {
            dominantTenGod: dominant.label,
            value: dominant.value,
            percent: dominant.percent || 0,
            timeUnknownLimited: timeUnknown
        }
    });
}

function buildStrengthSignal(result: SajuCalculationResult, baseConfidence: number, dayPillar: any, monthPillar: any, timeUnknown: boolean = false): InterpretationSignal | null {
    if (!dayPillar?.stem || !monthPillar?.branch) return null;
    let inferred = inferStrength(result, dayPillar, monthPillar);
    if (!inferred) return null;

    return signal({
        signalId: 'sig-strength-balance',
        category: 'strength',
        label: `신강/신약: ${inferred.label} ${inferred.score}`,
        summary: timeUnknown
            ? '월지와 오행 분포를 기준으로 일간의 힘을 추정한 내부 신호입니다. 생시 미정이라 시주 보정은 제외됩니다.'
            : '월지와 오행 분포를 기준으로 일간의 힘을 추정한 내부 신호입니다.',
        strength: clamp(inferred.label === '중화' ? 0.45 + Math.abs(inferred.score - 50) / 120 : 0.58 + Math.abs(inferred.score - 50) / 100),
        confidence: baseConfidence,
        polarity: inferred.label === '중화' ? 'neutral' : 'mixed',
        domains: ['energy_balance', 'self_pressure', 'support'],
        evidenceIds: compact([
            `strength:${inferred.label}:${inferred.score}`,
            `dayElement:${inferred.dayElement}`,
            `monthBranch:${monthPillar.branch.name}:${monthPillar.branch.element}`,
            `element:${inferred.dayElement}:value:${inferred.dayElementValue}`,
            `element:${inferred.resourceElement}:value:${inferred.resourceElementValue}`,
            `element:${inferred.outputElement}:value:${inferred.outputElementValue}`,
            `element:${inferred.wealthElement}:value:${inferred.wealthElementValue}`,
            `element:${inferred.officerElement}:value:${inferred.officerElementValue}`
        ]),
        allowedClaims: [
            `${inferred.label}에 가까운 에너지 운용으로 볼 수 있다.`,
            '강약 판단은 월지, 생조/설기/극제 관계, 오행 분포를 함께 본다.',
            timeUnknown ? '생시 미정에서는 시주가 보태는 생조·설기·극제 신호를 확정하지 않는다.' : ''
        ],
        blockedClaims: [
            '신강/신약을 좋고 나쁨의 우열로 말하지 않는다.',
            '강약 추정만으로 성공, 실패, 건강, 재물 결과를 확정하지 않는다.'
        ],
        meta: inferred
    });
}

function buildInteractionSignal(result: SajuCalculationResult, baseConfidence: number, timeUnknown: boolean = false): InterpretationSignal {
    let active = (result.interactions || []).filter((section: any) => section?.active && section.label !== '궁성' && section.items?.length);
    let items = active.flatMap((section: any) => section.items.map((item: string) => ({ section: section.label, item: item })));
    let hasItems = items.length > 0;

    return signal({
        signalId: 'sig-active-interactions',
        category: 'interaction',
        label: hasItems ? `합충형파해: ${active.map((section: any) => section.label).join('·')}` : '합충형파해: 두드러진 감지 없음',
        summary: hasItems
            ? (timeUnknown ? '천간/지지 관계에서 활성 조합을 해석 후보로 분리하되, 시주 포함 합충형파해는 생시 미정으로 제외합니다.' : '천간/지지 관계에서 활성 조합을 해석 후보로 분리합니다.')
            : (timeUnknown ? '년주·월주·일주 사이의 활성 조합이 없습니다. 시주 포함 합충형파해는 생시 미정으로 판단하지 않습니다.' : '계산된 활성 조합이 없으므로 관계 해석을 만들어내지 않습니다.'),
        strength: hasItems ? clamp(0.48 + Math.min(items.length, 5) * 0.08) : 0.22,
        confidence: baseConfidence,
        polarity: hasItems ? 'mixed' : 'neutral',
        domains: ['interaction', 'timing', 'relationship_pattern'],
        evidenceIds: hasItems ? items.map((entry: any) => `interaction:${entry.section}:${entry.item}`) : ['interaction:none'],
        allowedClaims: hasItems
            ? ['감지된 합충형파해를 변화성, 긴장, 결합 신호 후보로 볼 수 있다.', '관계 해석은 활성 조합과 위치를 함께 확인한다.']
            : ['합충형파해보다 일간, 월지, 오행 분포를 우선 근거로 삼는다.'],
        blockedClaims: [
            '계산되지 않은 합충형파해를 추가하지 않는다.',
            '합충형파해 하나로 이별, 사고, 성공 같은 사건을 단정하지 않는다.',
            timeUnknown ? '생시 미정일 때 시주가 포함되는 합충형파해를 있는 것처럼 말하지 않는다.' : ''
        ],
        meta: {
            activeLabels: active.map((section: any) => section.label),
            items: items.map((entry: any) => entry.item),
            timeUnknownLimited: timeUnknown
        }
    });
}

function buildStarSignal(result: SajuCalculationResult, baseConfidence: number, timeUnknown: boolean = false): InterpretationSignal {
    let nobleStars = result.nobleStars || [];
    let specialStars = result.specialStars || [];
    let stars = [
        ...nobleStars.map((star: any) => ({ ...star, group: 'noble' })),
        ...specialStars.map((star: any) => ({ ...star, group: 'special' }))
    ];
    let hasStars = stars.length > 0;
    let evidence = hasStars
        ? stars.slice(0, 8).map((star: any) => `star:${star.group}:${star.name}:${star.location || star.target || ''}`)
        : ['star:none'];

    return signal({
        signalId: 'sig-major-stars',
        category: 'star',
        label: hasStars ? `주요 신살: ${stars.slice(0, 4).map((star: any) => star.name).join('·')}` : '주요 신살: 두드러진 계산값 없음',
        summary: hasStars
            ? (timeUnknown ? '계산된 귀인/신살을 보조 해석 신호로 분리하되, 시지 기준 신살은 생시 미정으로 제외합니다.' : '계산된 귀인/신살을 보조 해석 신호로 분리합니다.')
            : (timeUnknown ? '표시할 신살 계산값이 없습니다. 시지 기준 신살은 생시 미정으로 확장하지 않습니다.' : '표시할 신살 계산값이 없어 신살 해석을 확장하지 않습니다.'),
        strength: hasStars ? clamp(0.38 + Math.min(stars.length, 6) * 0.07) : 0.2,
        confidence: baseConfidence,
        polarity: hasStars ? 'mixed' : 'neutral',
        domains: ['star', 'support', 'caution'],
        evidenceIds: evidence,
        allowedClaims: hasStars
            ? ['계산된 신살은 보조 근거로만 사용한다.', '귀인/신살은 위치와 반복 여부를 함께 본다.']
            : ['신살보다 원국, 오행, 십성 근거를 우선한다.'],
        blockedClaims: [
            '계산되지 않은 신살을 임의로 만들지 않는다.',
            '신살 하나로 운명적 사건이나 직업, 결혼, 재물 결과를 단정하지 않는다.',
            timeUnknown ? '생시 미정일 때 시지 기준 신살·귀인을 확정하지 않는다.' : ''
        ],
        meta: {
            nobleStars: nobleStars.map((star: any) => star.name),
            specialStars: specialStars.map((star: any) => star.name),
            timeUnknownLimited: timeUnknown
        }
    });
}

function inferStrength(result: SajuCalculationResult, dayPillar: any, monthPillar: any) {
    let dayElement = dayPillar.stem.element;
    let relations = result.elementRelations || {};
    let resourceElement = relations.resourceElement || producerOf(dayElement);
    let outputElement = relations.outputElement || producesElement(dayElement);
    let wealthElement = relations.wealthElement || controlsElement(dayElement);
    let officerElement = relations.officerElement || controlledByElement(dayElement);
    let byElement: any = {};
    normalizeElements(result.elements || []).forEach((item: any) => byElement[item.label] = item.value);
    let seasonalBonus = monthPillar.branch.element === dayElement ? 1.2 : monthPillar.branch.element === resourceElement ? 0.8 : 0;
    let support = (byElement[dayElement] || 0) + (byElement[resourceElement] || 0) * 0.85 + seasonalBonus;
    let pressure = (byElement[outputElement] || 0) * 0.65 + (byElement[wealthElement] || 0) * 0.8 + (byElement[officerElement] || 0) * 0.9;
    let score = Math.max(5, Math.min(95, Math.round(50 + (support - pressure) * 8)));
    let label = score >= 65 ? '신강' : score <= 43 ? '신약' : '중화';

    return {
        label: label,
        score: score,
        dayElement: dayElement,
        resourceElement: resourceElement,
        outputElement: outputElement,
        wealthElement: wealthElement,
        officerElement: officerElement,
        dayElementValue: byElement[dayElement] || 0,
        resourceElementValue: byElement[resourceElement] || 0,
        outputElementValue: byElement[outputElement] || 0,
        wealthElementValue: byElement[wealthElement] || 0,
        officerElementValue: byElement[officerElement] || 0,
        seasonalBonus: seasonalBonus,
        support: Math.round(support * 10) / 10,
        pressure: Math.round(pressure * 10) / 10
    };
}

function signal(data: InterpretationSignal): InterpretationSignal {
    return {
        ...data,
        strength: clamp(data.strength),
        confidence: clamp(data.confidence),
        evidenceIds: unique(compact(data.evidenceIds)),
        allowedClaims: compact(data.allowedClaims),
        blockedClaims: compact(data.blockedClaims)
    };
}

function normalizeElements(elements: any[]) {
    let byLabel: any = {};
    for (let item of elements || []) {
        if (!item?.label) continue;
        byLabel[item.label] = {
            label: item.label,
            value: Number(item.value || 0),
            percent: Number(item.percent || 0),
            tone: item.tone || ''
        };
    }
    for (let label of ELEMENT_LABELS) {
        if (!byLabel[label]) byLabel[label] = { label: label, value: 0, percent: 0, tone: '' };
    }
    return ELEMENT_LABELS.map((label: string) => byLabel[label]).sort((a: any, b: any) => b.value - a.value);
}

function findPillarDetail(result: SajuCalculationResult, part: string) {
    return (result.pillarDetails || []).find((item: any) => item.part === part);
}

function confidenceValue(value: any) {
    let numberValue = Number(value);
    if (Number.isNaN(numberValue)) return 1;
    return clamp(numberValue);
}

function timeLimitedConfidence(baseConfidence: number, category: string) {
    let limits: any = {
        element_balance: 0.72,
        ten_god: 0.68,
        strength: 0.72,
        interaction: 0.65,
        star: 0.65
    };
    return clamp(Math.min(baseConfidence, limits[category] || 0.7));
}

function clamp(value: number) {
    return Math.max(0, Math.min(1, Math.round(value * 100) / 100));
}

function compact(values: string[]) {
    return (values || []).filter((value: string) => !!value);
}

function unique(values: string[]) {
    let seen: any = {};
    return values.filter((value: string) => {
        if (seen[value]) return false;
        seen[value] = true;
        return true;
    });
}

function producesElement(element: string) {
    let flow: any = { 목: '화', 화: '토', 토: '금', 금: '수', 수: '목' };
    return flow[element] || '토';
}

function producerOf(element: string) {
    let flow: any = { 목: '수', 화: '목', 토: '화', 금: '토', 수: '금' };
    return flow[element] || '화';
}

function controlsElement(element: string) {
    let flow: any = { 목: '토', 토: '수', 수: '화', 화: '금', 금: '목' };
    return flow[element] || '수';
}

function controlledByElement(element: string) {
    let flow: any = { 목: '금', 화: '수', 토: '목', 금: '화', 수: '토' };
    return flow[element] || '목';
}
